import React from "react";
import type { OptionTs } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";
import * as Option from "@utils/Option";
import EmptyView from "@view/EmptyView";
import * as styles from "@styles/PageCounter.css";

export type SdeWorkbookPhase = "idle" | "ready" | "submitting" | "success" | "error";

export interface SdeWorkbookUploadPanelProps {
  readonly phase: SdeWorkbookPhase;
  readonly selectedFile: OptionTs<File>;
  readonly message: string;
  readonly onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Ok;
  readonly onSubmit: () => Promise<Ok>;
}

const SelectedFilePanel: React.FC<{ readonly selectedFile: OptionTs<File> }> = ({ selectedFile }) =>
  Option.fold(
    selectedFile,
    () => <EmptyView />,
    (file) => <p className={styles.selected}>Selected: {file.name} is ready for SDE workbook generation.</p>,
  );

const MessagePanel: React.FC<{ readonly phase: SdeWorkbookPhase; readonly message: string }> = ({ phase, message }) => {
  const standard = <div className={styles.statusCard} role="status">{message}</div>;
  const byPhase: Record<SdeWorkbookPhase, React.ReactElement> = {
    idle: standard,
    ready: standard,
    submitting: standard,
    success: standard,
    error: <div className={styles.statusCardError} role="alert">{message}</div>,
  };
  return byPhase[phase];
};

const SdeWorkbookUploadPanel: React.FC<SdeWorkbookUploadPanelProps> = ({
  phase,
  selectedFile,
  message,
  onFileChange,
  onSubmit,
}) => {
  const hasSelectedFile = Option.fold(selectedFile, () => false, () => true);
  const submitting = phase === "submitting";
  const buttonLabel = submitting ? "Generating..." : "Generate SDE workbook";

  return (
    <div className={styles.card}>
      <label className={styles.uploadLabel} htmlFor="sde-workbook-pdf-input">Tax return PDF</label>
      <p className={styles.selected}>V1 supports the DA2024 canonical fixture and returns a workbook download immediately.</p>
      <input
        id="sde-workbook-pdf-input"
        data-testid="sde-workbook-pdf-input"
        className={styles.input}
        type="file"
        accept="application/pdf,.pdf"
        onChange={onFileChange}
        disabled={submitting}
      />
      <SelectedFilePanel selectedFile={selectedFile} />
      <button className={styles.button} data-testid="sde-workbook-submit" onClick={onSubmit} disabled={!hasSelectedFile || submitting}>
        {buttonLabel}
      </button>
      <MessagePanel phase={phase} message={message} />
    </div>
  );
};

export default SdeWorkbookUploadPanel;
